import { DecimalPipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { BreakpointService } from '@ecm/consumer';

import { FileSizePipe } from '../../pipes/file-size/file-size.pipe';
import {
    DEFAULT_FILE_UPLOAD_ERROR_MESSAGES,
    EcmFileUploadError,
    EcmFileUploadErrorMessages,
    EcmFileUploadItem,
} from './material-file-upload.model';

/**
 * 📎 Shared presentational component for file upload with validation
 *
 * Handles file selection, extension/size/count/duplicate validation,
 * optional base64 conversion, and error display.
 *
 * @example
 * ```html
 * <ecm-mat-file-upload
 *   [acceptedFileTypes]="['png', 'jpg', 'pdf']"
 *   [maxSizeMB]="3"
 *   [maxFiles]="4"
 *   (filesChange)="onFilesChange($event)"
 * />
 * ```
 */
@Component({
    selector: 'ecm-mat-file-upload',
    standalone: true,
    imports: [FileSizePipe, DecimalPipe],
    templateUrl: './material-file-upload.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MaterialFileUploadComponent {
    /** 📦 Required inputs — file constraints */
    protected readonly acceptedFileTypes = input.required<string[]>();
    protected readonly maxSizeMB = input.required<number>();

    /** 📦 Optional inputs — customization */
    protected readonly maxFiles = input<number>(1);
    protected readonly multiple = input<boolean>(true);
    protected readonly convertToBase64 = input<boolean>(false);
    protected readonly disabled = input<boolean>(false);
    protected readonly files = input<EcmFileUploadItem[]>([]);
    protected readonly buttonLabel = input<string>('Ajouter un fichier');
    protected readonly errorMessages = input<EcmFileUploadErrorMessages>({});

    /** 📤 Output events */
    protected readonly filesChange = output<EcmFileUploadItem[]>();
    protected readonly fileError = output<EcmFileUploadError>();

    /** ♿ Exposed ref for parent scroll-to-error (e.g. via viewChild(MaterialFileUploadComponent)) */
    protected readonly errorElementRef = viewChild<ElementRef<HTMLElement>>('fileUploadError');

    /** 🔥 Internal state */
    protected readonly attachedFiles = signal<EcmFileUploadItem[]>([]);
    protected readonly currentError = signal<EcmFileUploadError | undefined>(undefined);

    private readonly _breakpointSvc = inject(BreakpointService);

    protected readonly isMobileWidth = toSignal(this._breakpointSvc.isMobileWidth());

    /** ✅ Computed: accepted file types string for native input accept attribute */
    protected readonly acceptedFileTypesString = computed(() =>
        this.acceptedFileTypes()
            .map((type) => `.${type}`)
            .join(','),
    );

    /** 📊 Computed: remaining attachment size in MB */
    protected readonly remainingSize = computed(() => {
        const usedSizeMB = this.attachedFiles().reduce((acc, item) => acc + item.file.size / (1024 * 1024), 0);
        return Math.max(this.maxSizeMB() - usedSizeMB, 0);
    });

    /** 📊 Computed: remaining attachment count */
    protected readonly remainingCount = computed(() => Math.max(this.maxFiles() - this.attachedFiles().length, 0));

    /** 📊 Computed: whether max files limit is reached */
    protected readonly isMaxFilesReached = computed(() => this.attachedFiles().length >= this.maxFiles());

    /** 📊 Computed: resolved error message string from currentError + merged messages */
    protected readonly errorMessage = computed(() => {
        const error = this.currentError();
        if (!error) return undefined;

        const custom = this.errorMessages();

        return custom[error] ?? DEFAULT_FILE_UPLOAD_ERROR_MESSAGES[error];
    });

    /** 🔄 Effect: sync attachedFiles when external files input changes (for parent reset) */
    private readonly _syncFilesEffect = effect(() => {
        const externalFiles = this.files();
        this.attachedFiles.set(externalFiles);
        this.currentError.set(undefined);
    });

    /**
     * 📎 Handles file selection from the native input, validates and adds files
     */
    protected async onFilesSelected(event: Event): Promise<void> {
        const inputEl = event.target as HTMLInputElement;
        const newFiles = Array.from(inputEl.files ?? []);
        const currentFiles = this.attachedFiles();
        const accepted = this.acceptedFileTypes().map((t) => t.toLowerCase());

        this.currentError.set(undefined);

        // 1️⃣ Check total file count
        if (currentFiles.length + newFiles.length > this.maxFiles()) {
            this._setError('maxFiles');
            inputEl.value = '';
            return;
        }

        // 2️⃣ Validate each file extension
        for (const file of newFiles) {
            const ext = file.name.split('.').pop()?.toLowerCase();
            if (!ext || !accepted.includes(ext)) {
                this._setError('invalidType');
                inputEl.value = '';
                return;
            }
        }

        // 3️⃣ Check duplicates (same name AND size)
        for (const file of newFiles) {
            const isDuplicate = currentFiles.some(
                (existing) => existing.file.name === file.name && existing.file.size === file.size,
            );
            if (isDuplicate) {
                this._setError('duplicate');
                inputEl.value = '';
                return;
            }
        }

        // 4️⃣ Check total size
        const currentSizeMB = currentFiles.reduce((acc, item) => acc + item.file.size / (1024 * 1024), 0);
        const newSizeMB = newFiles.reduce((acc, file) => acc + file.size / (1024 * 1024), 0);
        if (currentSizeMB + newSizeMB > this.maxSizeMB()) {
            this._setError('maxSize');
            inputEl.value = '';
            return;
        }

        // 5️⃣ Build upload items (with optional base64 conversion)
        let items: EcmFileUploadItem[];
        if (this.convertToBase64()) {
            items = await Promise.all(newFiles.map((file) => this._readFileAsBase64(file)));
        } else {
            items = newFiles.map((file) => ({ file }));
        }

        // 6️⃣ Add files and emit
        const updated = [...currentFiles, ...items];
        this.attachedFiles.set(updated);
        this.filesChange.emit(updated);

        // Reset native input so re-selecting the same file triggers change
        inputEl.value = '';
    }

    /**
     * 🗑️ Removes a file at the given index
     */
    protected removeFile(index: number): void {
        this.attachedFiles.update((files) => files.filter((_, i) => i !== index));
        this.currentError.set(undefined);
        this.filesChange.emit(this.attachedFiles());
    }

    /**
     * 🔒 Sets an error and emits the fileError output
     */
    private _setError(error: EcmFileUploadError): void {
        this.currentError.set(error);
        this.fileError.emit(error);
    }

    /**
     * 📖 Reads a file as base64 data URL string using FileReader
     */
    private _readFileAsBase64(file: File): Promise<EcmFileUploadItem> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result as string;
                resolve({ file, base64data: result.split(',')[1] });
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
        });
    }
}
